import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useCurrency } from '../context/CurrencyContext';
import { 
  Minus, Plus, Trash2, MapPin, ShoppingCart, CreditCard, Package, UtensilsCrossed, Calendar, Users, Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AddressAutocomplete } from '../components/AddressAutocomplete';
import { toast } from 'sonner';

export default function CartPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const { cart, updateQuantity, clearCart, getSubtotal, getDeliveryFee } = useCart();
  const { formatPrice } = useCurrency();
  const [loading, setLoading] = useState(false);
  const [orderType, setOrderType] = useState('delivery');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [phone, setPhone] = useState(user?.phone || '');
  const [instructions, setInstructions] = useState('');
  const [serviceStatus, setServiceStatus] = useState({ delivery_enabled: true, takeaway_enabled: true, table_booking_enabled: false });
  // Table booking specific state
  const [bookingDate, setBookingDate] = useState('');
  const [bookingTime, setBookingTime] = useState('');
  const [partySize, setPartySize] = useState(2);

  // Fetch restaurant service status
  useEffect(() => {
    if (cart.restaurant?.id) {
      fetchServiceStatus();
    }
  }, [cart.restaurant?.id]);

  const fetchServiceStatus = async () => {
    try {
      const response = await api.get(`/restaurants/${cart.restaurant.id}/service-status`);
      setServiceStatus(response.data);
      // Auto-select available order type
      if (!response.data.delivery_enabled && response.data.takeaway_enabled) {
        setOrderType('takeaway');
      }
    } catch (error) {
      console.error('Failed to fetch service status:', error);
    }
  };

  const handleCheckout = async () => {
    if (!user) {
      toast.error(t('error.unauthorized'));
      navigate('/login?redirect=/cart');
      return;
    }

    // Validate delivery address only for delivery orders
    if (orderType === 'delivery' && (!address || !city || !postalCode)) {
      toast.error(t('error.validation'));
      return;
    }

    // Validate table booking details
    if (orderType === 'dine_in' && (!bookingDate || !bookingTime)) {
      toast.error(t('error.validation'));
      return;
    }

    setLoading(true);

    try {
      const orderItems = [];
      for (let i = 0; i < cart.items.length; i++) {
        const item = cart.items[i];
        orderItems.push({
          id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
        });
      }

      const orderData = {
        restaurant_id: cart.restaurant.id,
        items: orderItems,
        order_type: orderType,
        delivery_phone: phone,
        delivery_instructions: instructions,
      };

      // Only include address for delivery orders
      if (orderType === 'delivery') {
        orderData.delivery_address = address;
        orderData.delivery_city = city;
        orderData.delivery_postal_code = postalCode;
      }

      // Include booking details for dine-in orders
      if (orderType === 'dine_in') {
        orderData.booking_date = bookingDate;
        orderData.booking_time = bookingTime;
        orderData.party_size = partySize;
      }

      const orderResponse = await api.post('/orders', orderData);
      const orderId = orderResponse.data.order_id;

      // For takeaway and dine_in orders, skip payment and show confirmation
      if (orderType === 'takeaway' || orderType === 'dine_in') {
        clearCart();
        toast.success(t('success.orderPlaced'));
        navigate(`/orders/${orderId}`);
        return;
      }

      // For delivery orders, redirect to Stripe payment
      const paymentResponse = await api.post('/payments/checkout', {
        order_id: orderId,
        origin_url: window.location.origin,
      });

      window.location.href = paymentResponse.data.checkout_url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create order');
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals based on order type
  const calculateDeliveryFee = () => orderType === 'delivery' ? getDeliveryFee() : 0;
  const calculateServiceFee = () => orderType === 'delivery' ? 0.50 : 0;
  const calculateTotal = () => getSubtotal() + calculateDeliveryFee() + calculateServiceFee();

  if (cart.items.length === 0) {
    return (
      <>
        <Helmet>
          <title>Your Cart - Hambre</title>
          <meta name="description" content="Review your order and checkout securely. Fast delivery from your favorite local restaurants." />
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div className="min-h-screen bg-slate-50 pt-24">
          <div className="max-w-2xl mx-auto px-4 text-center py-16">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-slate-100 flex items-center justify-center">
              <ShoppingCart className="w-10 h-10 text-slate-400" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">{t('cart.empty')}</h1>
            <p className="text-slate-500 mb-6">{t('cart.addItems')}</p>
            <Button 
              className="btn-primary"
              onClick={() => navigate('/restaurants')}
              data-testid="browse-restaurants"
            >
              {t('cart.browseRestaurants')}
            </Button>
          </div>
        </div>
      </>
    );
  }

  const renderCartItems = () => {
    const items = [];
    for (let i = 0; i < cart.items.length; i++) {
      const item = cart.items[i];
      items.push(
        <div 
          key={item.id} 
          className="flex items-center gap-4 p-3 rounded-lg bg-slate-50"
          data-testid={`cart-item-${item.id}`}
        >
          <div className="flex-1">
            <h3 className="font-medium text-slate-900">{item.name}</h3>
            <p className="text-sm text-orange-500 font-medium">{formatPrice(item.price)}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 rounded-full"
              onClick={() => updateQuantity(item.id, item.quantity - 1)}
              data-testid={`decrease-${item.id}`}
            >
              <Minus className="w-4 h-4" />
            </Button>
            <span className="w-8 text-center font-medium">{item.quantity}</span>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 rounded-full"
              onClick={() => updateQuantity(item.id, item.quantity + 1)}
              data-testid={`increase-${item.id}`}
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          <span className="font-semibold text-slate-900 w-20 text-right">
            {formatPrice(item.price * item.quantity)}
          </span>
        </div>
      );
    }
    return items;
  };

  return (
    <>
      <Helmet>
        <title>Your Cart - Hambre</title>
        <meta name="description" content="Review your order and checkout securely. Fast delivery from your favorite local restaurants." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="min-h-screen bg-slate-50 pt-24 pb-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8">{t('checkout.title')}</h1>

          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-semibold text-slate-900">
                    {cart.restaurant.name}
                  </h2>
                  <Button variant="ghost" size="sm" onClick={clearCart} className="text-red-500">
                    <Trash2 className="w-4 h-4 mr-1" /> {t('cart.clearCart')}
                  </Button>
                </div>
                <div className="space-y-4">
                {renderCartItems()}
              </div>
            </div>

            {/* Order Type Selection */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">{t('checkout.deliveryDetails')}</h2>
              <div className="grid grid-cols-3 gap-3">
                <button
                  type="button"
                  onClick={() => setOrderType('delivery')}
                  disabled={!serviceStatus.delivery_enabled}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    orderType === 'delivery' 
                      ? 'border-orange-500 bg-orange-50' 
                      : 'border-slate-200 hover:border-slate-300'
                  } ${!serviceStatus.delivery_enabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  data-testid="order-type-delivery"
                >
                  <div className={`w-10 h-10 mx-auto mb-2 rounded-full flex items-center justify-center ${
                    orderType === 'delivery' ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-500'
                  }`}>
                    <MapPin className="w-5 h-5" />
                  </div>
                  <h3 className="font-semibold text-slate-900 text-sm">{t('orderType.delivery')}</h3>
                  <p className="text-xs text-slate-500 mt-1">{t('orderType.deliveryDesc')}</p>
                  {!serviceStatus.delivery_enabled && (
                    <p className="text-xs text-red-500 mt-1">{t('menu.unavailable')}</p>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => setOrderType('takeaway')}
                  disabled={!serviceStatus.takeaway_enabled}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    orderType === 'takeaway' 
                      ? 'border-orange-500 bg-orange-50' 
                      : 'border-slate-200 hover:border-slate-300'
                  } ${!serviceStatus.takeaway_enabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  data-testid="order-type-takeaway"
                >
                  <div className={`w-10 h-10 mx-auto mb-2 rounded-full flex items-center justify-center ${
                    orderType === 'takeaway' ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-500'
                  }`}>
                    <Package className="w-5 h-5" />
                  </div>
                  <h3 className="font-semibold text-slate-900 text-sm">{t('orderType.takeaway')}</h3>
                  <p className="text-xs text-slate-500 mt-1">{t('orderType.takeawayDesc')}</p>
                  {!serviceStatus.takeaway_enabled && (
                    <p className="text-xs text-red-500 mt-1">{t('menu.unavailable')}</p>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => setOrderType('dine_in')}
                  disabled={!serviceStatus.table_booking_enabled}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    orderType === 'dine_in' 
                      ? 'border-orange-500 bg-orange-50' 
                      : 'border-slate-200 hover:border-slate-300'
                  } ${!serviceStatus.table_booking_enabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  data-testid="order-type-dine-in"
                >
                  <div className={`w-10 h-10 mx-auto mb-2 rounded-full flex items-center justify-center ${
                    orderType === 'dine_in' ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-500'
                  }`}>
                    <UtensilsCrossed className="w-5 h-5" />
                  </div>
                  <h3 className="font-semibold text-slate-900 text-sm">{t('orderType.dineIn')}</h3>
                  <p className="text-xs text-slate-500 mt-1">{t('orderType.dineInDesc')}</p>
                  {!serviceStatus.table_booking_enabled && (
                    <p className="text-xs text-red-500 mt-1">{t('menu.unavailable')}</p>
                  )}
                </button>
              </div>
            </div>

            {/* Delivery Address - Only show for delivery orders */}
            {orderType === 'delivery' && (
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-orange-500" />
                  {t('checkout.delivery')}
                </h2>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label htmlFor="address">{t('restaurant.address')}</Label>
                    <AddressAutocomplete
                      value={address}
                      onChange={setAddress}
                      onSelect={(data) => {
                        if (data) {
                          setAddress(data.address);
                          if (data.components?.city) setCity(data.components.city);
                          if (data.components?.postalCode) setPostalCode(data.components.postalCode);
                        }
                      }}
                      placeholder={t('placeholder.address')}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="city">{t('placeholder.city')}</Label>
                    <Input
                      id="city"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder={t('placeholder.city')}
                      className="mt-1"
                      data-testid="city-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="postal_code">{t('placeholder.postalCode')}</Label>
                    <Input
                      id="postal_code"
                      value={postalCode}
                      onChange={(e) => setPostalCode(e.target.value)}
                      placeholder="SW1A 1AA"
                      className="mt-1"
                      data-testid="postal-code-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">{t('placeholder.phone')}</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="+44 123 456 7890"
                      className="mt-1"
                      data-testid="phone-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="instructions">{t('checkout.specialInstructions')}</Label>
                    <Input
                      id="instructions"
                      value={instructions}
                      onChange={(e) => setInstructions(e.target.value)}
                      placeholder={t('placeholder.instructions')}
                      className="mt-1"
                      data-testid="instructions-input"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Takeaway Info - Only show for takeaway orders */}
            {orderType === 'takeaway' && (
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <UtensilsCrossed className="w-5 h-5 text-orange-500" />
                  Pickup Details
                </h2>
                <div className="bg-orange-50 rounded-xl p-4 mb-4">
                  <p className="text-sm text-orange-800">
                    <strong>Restaurant:</strong> {cart.restaurant.name}
                  </p>
                  <p className="text-sm text-orange-800 mt-1">
                    <strong>Address:</strong> {cart.restaurant.address || 'Address available after order'}
                  </p>
                  <p className="text-sm text-orange-700 mt-2">
                    You'll pay at the restaurant when you collect your order.
                  </p>
                </div>
                <div>
                  <Label htmlFor="phone">Phone Number (for order updates)</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+44 123 456 7890"
                    className="mt-1"
                    data-testid="phone-input"
                  />
                </div>
              </div>
            )}

            {/* Dine-In Booking - Only show for dine_in orders */}
            {orderType === 'dine_in' && (
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-orange-500" />
                  Table Reservation
                </h2>
                <div className="bg-green-50 rounded-xl p-4 mb-4">
                  <p className="text-sm text-green-800">
                    <strong>Restaurant:</strong> {cart.restaurant.name}
                  </p>
                  <p className="text-sm text-green-800 mt-1">
                    <strong>Address:</strong> {cart.restaurant.address || 'Address available after booking'}
                  </p>
                  <p className="text-sm text-green-700 mt-2">
                    Your pre-order will be ready when you arrive. Pay at the restaurant.
                  </p>
                </div>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="booking-date" className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" /> Date
                    </Label>
                    <Input
                      id="booking-date"
                      type="date"
                      value={bookingDate}
                      onChange={(e) => setBookingDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="mt-1"
                      data-testid="booking-date"
                    />
                  </div>
                  <div>
                    <Label htmlFor="booking-time" className="flex items-center gap-1">
                      <Clock className="w-4 h-4" /> Time
                    </Label>
                    <Select value={bookingTime} onValueChange={setBookingTime}>
                      <SelectTrigger className="mt-1" data-testid="booking-time">
                        <SelectValue placeholder="Select time" />
                      </SelectTrigger>
                      <SelectContent>
                        {['12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '17:00', '17:30', '18:00', '18:30', '19:00', '19:30', '20:00', '20:30', '21:00'].map((time) => (
                          <SelectItem key={time} value={time}>{time}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="party-size" className="flex items-center gap-1">
                      <Users className="w-4 h-4" /> Party Size
                    </Label>
                    <Select value={String(partySize)} onValueChange={(v) => setPartySize(Number(v))}>
                      <SelectTrigger className="mt-1" data-testid="party-size">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4, 5, 6, 7, 8, 10, 12].map((size) => (
                          <SelectItem key={size} value={String(size)}>{size} {size === 1 ? 'person' : 'people'}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mt-4">
                  <Label htmlFor="phone">Phone Number (for booking confirmation)</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+44 123 456 7890"
                    className="mt-1"
                    data-testid="phone-input"
                  />
                </div>
                <div className="mt-4">
                  <Label htmlFor="instructions">Special Requests</Label>
                  <Input
                    id="instructions"
                    value={instructions}
                    onChange={(e) => setInstructions(e.target.value)}
                    placeholder="High chair, window seat, allergies..."
                    className="mt-1"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm p-6 sticky top-24">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Order Summary</h2>
              
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">{t('cart.subtotal')}</span>
                  <span>{formatPrice(getSubtotal())}</span>
                </div>
                {orderType === 'delivery' && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-slate-500">{t('cart.deliveryFee')}</span>
                      <span>{formatPrice(calculateDeliveryFee())}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">{t('cart.serviceFee')}</span>
                      <span>{formatPrice(0.50)}</span>
                    </div>
                  </>
                )}
                {(orderType === 'takeaway' || orderType === 'dine_in') && (
                  <div className="flex justify-between text-green-600">
                    <span>Delivery Fee</span>
                    <span>{formatPrice(0)} ({orderType === 'takeaway' ? 'Pickup' : 'Dine-in'})</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg pt-3 border-t border-slate-100">
                  <span>{t('cart.total')}</span>
                  <span className="text-orange-500">{formatPrice(calculateTotal())}</span>
                </div>
              </div>

              <Button
                className="btn-primary w-full mt-6"
                onClick={handleCheckout}
                disabled={loading}
                data-testid="checkout-button"
              >
                {loading ? 'Processing...' : (
                  <span className="flex items-center justify-center">
                    {orderType === 'delivery' ? (
                      <>
                        <CreditCard className="w-5 h-5 mr-2" />
                        Pay {formatPrice(calculateTotal())}
                      </>
                    ) : orderType === 'dine_in' ? (
                      <>
                        <Calendar className="w-5 h-5 mr-2" />
                        Book Table
                      </>
                    ) : (
                      <>
                        <Package className="w-5 h-5 mr-2" />
                        Place Order
                      </>
                    )}
                  </span>
                )}
              </Button>

              <p className="text-xs text-slate-400 text-center mt-4">
                {orderType === 'delivery' 
                  ? t('payment.stripeRedirect')
                  : orderType === 'dine_in'
                  ? t('payment.tableReserved')
                  : t('payment.payAtRestaurant')
                }
              </p>
            </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
